import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarCheck, Check, X, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { orgToday } from "@/lib/tz";

const CRIMSON = "var(--brand)";
const NONE = "__none__";
const todayStr = () => orgToday();
const fmtDue = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

const STATUS_STYLE = {
  Scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  Overdue: "bg-rose-50 text-rose-700 border-rose-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

// Office-only schedule of dates by which the Service Team must check in with the
// participant. "Overdue" is derived (Scheduled and past due), not stored.
export function ParticipantCheckIns({ participant, canEdit }) {
  const [rows, setRows] = useState(null);
  const [staff, setStaff] = useState([]);
  const [assignee, setAssignee] = useState(NONE);
  const [due, setDue] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(null); // id of the check-in being completed
  const [outcome, setOutcome] = useState("");

  const load = () => api.participantCheckIns(participant).then(setRows).catch(() => setRows([]));
  useEffect(() => { setRows(null); setCompleting(null); load(); }, [participant]);
  useEffect(() => { if (canEdit) api.staff().then(setStaff).catch(() => setStaff([])); }, [canEdit]);
  const staffOptions = (cur) => {
    const list = staff.filter((s) => (s.status || "Active") === "Active" || s.name === cur);
    return list.map((s) => <SelectItem key={s.name} value={s.name} className="text-sm">{s.display_name || s.name}</SelectItem>);
  };

  const add = async () => {
    if (!due) { toast.error("Choose the date the check-in is due."); return; }
    setBusy(true);
    try { await api.participantCheckInAdd(participant, { due_date: due, purpose, assignee: assignee === NONE ? "" : assignee }); setDue(""); setPurpose(""); setAssignee(NONE); toast.success("Check-in scheduled."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not schedule check-in."); }
    finally { setBusy(false); }
  };
  const update = async (id, body, msg) => {
    try { await api.participantCheckInUpdate(participant, id, body); toast.success(msg); setCompleting(null); setOutcome(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update check-in."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this check-in?")) return;
    try { await api.participantCheckInDelete(participant, id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  const today = todayStr();
  const shown = (rows || []).map((r) => ({ ...r, shownStatus: r.status === "Scheduled" && r.due_date < today ? "Overdue" : r.status }));
  const open = shown.filter((r) => r.status === "Scheduled");
  const closed = shown.filter((r) => r.status !== "Scheduled").sort((a, b) => b.due_date.localeCompare(a.due_date));

  const renderRow = (r) => (
    <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`checkin-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{fmtDue(r.due_date)}</span>
            <Badge className={`${STATUS_STYLE[r.shownStatus]} text-[9px] py-0`} data-testid={`checkin-status-${r.id}`}>{r.shownStatus}</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid={`checkin-assignee-${r.id}`}>
            {canEdit && r.status === "Scheduled" ? (
              <span className="inline-flex items-center gap-1.5">Assigned to
                <Select value={r.assignee || NONE} onValueChange={(v) => update(r.id, { assignee: v === NONE ? "" : v }, "Assignee updated.")}>
                  <SelectTrigger className="h-7 text-xs w-44 bg-white" data-testid={`checkin-assignee-select-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE} className="text-sm">Unassigned</SelectItem>{staffOptions(r.assignee)}</SelectContent>
                </Select>
              </span>
            ) : (r.assignee_name ? <>Assigned to {r.assignee_name}</> : "Unassigned")}
          </div>
          {r.purpose && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{r.purpose}</div>}
          {r.outcome && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap"><span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Outcome</span>{r.outcome}</div>}
          <div className="text-[10px] text-slate-400 mt-1.5">
            Set by {r.created_by_name || r.created_by} · {fmtLog(r.created_at)}
            {r.status === "Completed" && r.completed_at && <> · Completed by {r.completed_by_name} · {fmtLog(r.completed_at)}</>}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            {r.status === "Scheduled" ? (
              <>
                <button onClick={() => { setCompleting(r.id); setOutcome(""); }} className="text-slate-400 hover:text-emerald-600 p-1 rounded hover:bg-emerald-50" title="Mark completed" data-testid={`checkin-complete-${r.id}`}><Check className="h-4 w-4" /></button>
                <button onClick={() => update(r.id, { status: "Cancelled" }, "Check-in cancelled.")} className="text-slate-400 hover:text-amber-600 p-1 rounded hover:bg-amber-50" title="Cancel" data-testid={`checkin-cancel-${r.id}`}><X className="h-4 w-4" /></button>
              </>
            ) : (
              <button onClick={() => update(r.id, { status: "Scheduled" }, "Check-in reopened.")} className="text-slate-400 hover:text-sky-600 p-1 rounded hover:bg-sky-50" title="Reopen" data-testid={`checkin-reopen-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></button>
            )}
            <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50" title="Delete" data-testid={`checkin-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      {completing === r.id && (
        <div className="mt-2 border-t border-slate-100 pt-2" data-testid={`checkin-complete-form-${r.id}`}>
          <Textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Outcome of the check-in (optional)…" className="text-sm" rows={2} />
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setCompleting(null)}>Back</Button>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={() => update(r.id, { status: "Completed", outcome }, "Check-in completed.")} data-testid={`checkin-complete-confirm-${r.id}`}>Mark completed</Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="participant-checkins">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" />Check-ins</div>
      <p className="text-xs text-slate-500 mb-3">Set a date by which the Service Team needs to check in with this participant, and who is responsible. The assignee is emailed the day before it is due and again if it goes overdue. Office staff only.</p>
      {canEdit && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 mb-4" data-testid="checkin-form">
          <div className="flex gap-2 flex-wrap">
            <Input type="date" value={due} min={todayStr()} onChange={(e) => setDue(e.target.value)} className="h-9 text-sm bg-white w-44" data-testid="checkin-due" />
            <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="What the check-in is for (optional)" className="h-9 text-sm bg-white flex-1 min-w-[200px]" data-testid="checkin-purpose" />
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger className="h-9 text-sm bg-white w-48" data-testid="checkin-assignee"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent><SelectItem value={NONE} className="text-sm">Unassigned</SelectItem>{staffOptions(null)}</SelectContent>
            </Select>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={add} disabled={busy} className="h-9" data-testid="checkin-add">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Schedule
            </Button>
          </div>
        </div>
      )}
      {rows === null ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No check-ins scheduled.</div>
      ) : (
        <div className="space-y-4">
          {open.length > 0 && <div className="space-y-2" data-testid="checkins-open">{open.map(renderRow)}</div>}
          {closed.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">History</div>
              <div className="space-y-2" data-testid="checkins-history">{closed.map(renderRow)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
